import { Module } from 'vuex';
import {
  action,
  actionCreatorFactory,
  combineAction,
  combineMutation,
  mutation,
} from 'vuex-typescript-fsa/lib';
import { RootState } from '@/store';
import {
  Cv,
  CvCvSection,
  CvCvSectionResponse,
  CvDesignTemplate,
  CvSection,
  CvSectionProfile,
  CvSectionProfileResponse,
  UpdateCvPageBreaksRequest,
} from '@/../gen';
import { CvSectionProfileParams } from '@/containers/cv-section-profile/store';

export const namespace = 'cv_preview';
const actionCreator = actionCreatorFactory(namespace);

export const GetCv = actionCreator<{ cv_id: number }>('GET_CV');
export const CvFetched = actionCreator<Cv>('CV_FETCHED');
export const CvDesignTemplateFetched = actionCreator<CvDesignTemplate>(
  'CV_DESIGN_TEMPLATE_FETCHED'
);
export const GetCvCvSections = actionCreator<{ cv_id: number }>(
  'GET_CV_CV_SECTIONS'
);
export const CvCvSectionsFetched = actionCreator<CvCvSectionResponse[]>(
  'SET_CV_CV_SECTIONS'
);
export const GetCvSectionProfile = actionCreator<CvSectionProfileParams>(
  'GET_CV_SECTION_PROFILE'
);
export const CvSectionProfileFetched = actionCreator<CvSectionProfileResponse>(
  'SET_CV_SECTION_PROFILE'
);
export const UpdateCvPageBreaks = actionCreator<{
  cvId: number;
  body: UpdateCvPageBreaksRequest;
}>('UPDATE_CV_PAGE_BREAKS');

export const ResetState = actionCreator<null>('RESET_STATE');

export interface CvCvSectionWithSection extends CvCvSection {
  section: CvSection;
  sectionItems: any[];
}
export interface State {
  cv: Cv;
  cvSections: CvCvSectionWithSection[];
  cvSectionProfile: CvSectionProfile;
  cvDesignTemplate: CvDesignTemplate;
}

const initialState = (): State => {
  return {
    cv: null,
    cvSections: [],
    cvSectionProfile: null,
    cvDesignTemplate: null,
  };
};

export const module: Module<State, RootState> = {
  namespaced: true,
  state: initialState,
  actions: combineAction(
    action(GetCv, async function(context, action) {
      const { data } = await this.proffApi.getCv(action.payload.cv_id);
      context.commit(CvFetched(data.data.attributes));
      context.commit(CvDesignTemplateFetched(data.included[0].attributes));
    }),
    action(GetCvCvSections, async function(context, action) {
      const { data } = await this.proffApi.listCvCvSections(
        action.payload.cv_id
      );
      context.commit(CvCvSectionsFetched(data.data));
    }),
    action(GetCvSectionProfile, async function(context, action) {
      const { data } = await this.proffApi.getCvSectionProfile(
        action.payload.cv_id
      );
      context.commit(CvSectionProfileFetched(data.data));
    }),
    action(UpdateCvPageBreaks, async function(context, action) {
      const { data } = await this.proffApi.updateCvPageBreaks(
        action.payload.cvId,
        action.payload.body
      );
      context.commit(CvCvSectionsFetched(data.data));
    }),
    action(ResetState, function(context, action) {
      context.commit(ResetState(action.payload));
    })
  ),
  mutations: combineMutation(
    mutation(CvFetched, (state, action) => {
      state.cv = action.payload;
    }),
    mutation(CvDesignTemplateFetched, (state, action) => {
      state.cvDesignTemplate = action.payload;
    }),
    mutation(CvCvSectionsFetched, (state, action) => {
      state.cvSections = action.payload.map(item => {
        const { section, section_items } = item.links;
        return {
          ...item.attributes,
          // @ts-ignore FIXME
          section: section ? section.data.attributes : null,
          sectionItems: section_items
            ? // @ts-ignore FIXME
              section_items.data.map(sectionItem => sectionItem.attributes)
            : [],
        };
      });
    }),
    mutation(CvSectionProfileFetched, (state, action) => {
      state.cvSectionProfile = action.payload.attributes;
    }),
    mutation(ResetState, state => {
      Object.assign(state, initialState());
    })
  ),
  getters: {
    cv: state => state.cv,
    cvSections: state => state.cvSections,
    cvSectionProfile: state => state.cvSectionProfile,
    cvDesignTemplate: state => state.cvDesignTemplate,
  },
};
